"use client"

import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { cn } from "@/lib/utils"
import { openStockChart } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { ColumnFilter } from "@/components/ui/ColumnFilter"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { getTrades } from "@/lib/api/trading"
import type { OrderType, Trade } from "@/types/trading"

const PAGE_SIZE = 30

const ORDER_TYPE_LABEL: Record<OrderType, string> = { BUY: "매수", SELL: "매도" }
const LABEL_TO_ORDER_TYPE: Record<string, OrderType> = { 매수: "BUY", 매도: "SELL" }
const STATUS_LABEL: Record<string, string> = { PENDING: "대기", FILLED: "체결", CANCELLED: "취소" }

function OrderTypeBadge({ type }: { type: OrderType }) {
  return (
    <Badge
      variant="outline"
      className={
        type === "BUY"
          ? "border-red-500/40 bg-red-500/10 text-red-400"
          : "border-blue-500/40 bg-blue-500/10 text-blue-400"
      }
    >
      {ORDER_TYPE_LABEL[type]}
    </Badge>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "FILLED"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
      : status === "CANCELLED"
        ? "border-zinc-500/40 bg-zinc-500/10 text-zinc-400"
        : "border-yellow-500/40 bg-yellow-500/10 text-yellow-400"
  return (
    <Badge variant="outline" className={cls}>
      {STATUS_LABEL[status] ?? status}
    </Badge>
  )
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i} className="border-zinc-800">
          {Array.from({ length: 9 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full bg-zinc-800" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

// 1 2 3 ... n 형태의 페이지 번호 목록 (현재 페이지 좌우 1칸 + 처음/끝 고정)
function getPageList(current: number, total: number): (number | "ellipsis")[] {
  const pages: (number | "ellipsis")[] = []
  for (let p = 1; p <= total; p++) {
    if (p === 1 || p === total || Math.abs(p - current) <= 1) {
      pages.push(p)
    } else if (pages[pages.length - 1] !== "ellipsis") {
      pages.push("ellipsis")
    }
  }
  return pages
}

function TradePagination({
  page,
  totalPages,
  onChange,
}: {
  page: number
  totalPages: number
  onChange: (p: number) => void
}) {
  const pages = getPageList(page, totalPages)
  const linkClass = "border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            text="이전"
            onClick={(e) => {
              e.preventDefault()
              if (page > 1) onChange(page - 1)
            }}
            className={cn(linkClass, page === 1 && "pointer-events-none opacity-40")}
          />
        </PaginationItem>

        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <PaginationItem key={`e${i}`}>
              <PaginationEllipsis className="text-zinc-500" />
            </PaginationItem>
          ) : (
            <PaginationItem key={p}>
              <PaginationLink
                href="#"
                isActive={p === page}
                onClick={(e) => {
                  e.preventDefault()
                  onChange(p)
                }}
                className={
                  p === page
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/10"
                    : linkClass
                }
              >
                {p}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <PaginationNext
            href="#"
            text="다음"
            onClick={(e) => {
              e.preventDefault()
              if (page < totalPages) onChange(page + 1)
            }}
            className={cn(linkClass, page === totalPages && "pointer-events-none opacity-40")}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

function TradeRow({ t }: { t: Trade }) {
  return (
    <TableRow
      className="cursor-pointer border-zinc-800 text-zinc-200 hover:bg-zinc-800/60"
      onClick={() => openStockChart(t.stockCode)}
    >
      <TableCell className="text-zinc-300">{t.tradeDate}</TableCell>
      <TableCell className="font-medium">{t.stockName}</TableCell>
      <TableCell className="font-mono text-zinc-400">{t.stockCode}</TableCell>
      <TableCell>
        <OrderTypeBadge type={t.orderType} />
      </TableCell>
      <TableCell className="text-zinc-300">{t.conditionName ?? "-"}</TableCell>
      <TableCell className="text-right font-mono">
        {t.filledPrice != null ? t.filledPrice.toLocaleString("ko-KR") : "-"}
      </TableCell>
      <TableCell className="text-right font-mono">{t.filledQuantity.toLocaleString("ko-KR")}</TableCell>
      <TableCell className="text-right font-mono">{t.totalAmount.toLocaleString("ko-KR")}</TableCell>
      <TableCell>
        <StatusBadge status={t.orderStatus} />
      </TableCell>
    </TableRow>
  )
}

export function TradeHistoryTab() {
  const [page, setPage] = useState(1)
  const [date, setDate] = useState("")
  const [keyword, setKeyword] = useState("")
  const [orderTypeFilter, setOrderTypeFilter] = useState<Set<string>>(new Set())

  const singleOrderType =
    orderTypeFilter.size === 1 ? LABEL_TO_ORDER_TYPE[[...orderTypeFilter][0]] : undefined

  const { data, isLoading } = useQuery({
    queryKey: ["trades", page, date, keyword, singleOrderType],
    queryFn: () =>
      getTrades({
        page,
        pageSize: PAGE_SIZE,
        date: date || undefined,
        stockCode: keyword || undefined,
        orderType: singleOrderType,
      }),
    placeholderData: (prev) => prev,
  })

  const items = data?.items ?? []
  const totalPages = data?.totalPages ?? 1
  const hasFilter = date !== "" || keyword !== "" || orderTypeFilter.size > 0

  const resetFilters = () => {
    setDate("")
    setKeyword("")
    setOrderTypeFilter(new Set())
    setPage(1)
  }

  return (
    <div className="flex flex-col gap-3">
      {/* 필터 바 */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-zinc-500">날짜</label>
          <Input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value)
              setPage(1)
            }}
            className="w-40 border-zinc-700 bg-zinc-900 text-zinc-100"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-zinc-500">종목명/코드</label>
          <Input
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value)
              setPage(1)
            }}
            placeholder="예: 삼성전자, 005930"
            className="w-48 border-zinc-700 bg-zinc-900 text-zinc-100"
          />
        </div>
        {hasFilter && (
          <button
            onClick={resetFilters}
            className="h-8 rounded-md px-3 text-xs text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100"
          >
            필터 초기화
          </button>
        )}
        {data && <span className="ml-auto text-sm text-zinc-500">총 {data.totalCount.toLocaleString("ko-KR")}건</span>}
      </div>

      <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">날짜</TableHead>
              <TableHead className="text-zinc-400">종목명</TableHead>
              <TableHead className="text-zinc-400">종목코드</TableHead>
              <TableHead className="text-zinc-400">
                <div className="flex items-center gap-1">
                  구분
                  <ColumnFilter
                    values={["매수", "매도"]}
                    selected={new Set([...orderTypeFilter].map((t) => ORDER_TYPE_LABEL[t as OrderType]))}
                    onChange={(labels) => {
                      setOrderTypeFilter(new Set([...labels].map((l) => LABEL_TO_ORDER_TYPE[l])))
                      setPage(1)
                    }}
                  />
                </div>
              </TableHead>
              <TableHead className="text-zinc-400">조건식</TableHead>
              <TableHead className="text-right text-zinc-400">체결가</TableHead>
              <TableHead className="text-right text-zinc-400">수량</TableHead>
              <TableHead className="text-right text-zinc-400">총금액</TableHead>
              <TableHead className="text-zinc-400">상태</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : items.length === 0 ? (
              <TableRow className="border-zinc-800">
                <TableCell colSpan={9} className="py-12 text-center text-zinc-500">
                  매매내역이 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              items.map((t) => <TradeRow key={t.id} t={t} />)
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && <TradePagination page={page} totalPages={totalPages} onChange={setPage} />}
    </div>
  )
}
